/**
 * The Parent Genie conversation, shared by the full `/assistant` page and the floating
 * Assistant bubble, so a question asked in one is still there in the other and an answer
 * keeps arriving while the parent moves between pages.
 *
 * Split from the provider in `GenieChatContext.tsx` for the same reason `brand-profile.ts` is:
 * the provider calls Supabase, and a component that only reads the conversation should not
 * import the client — its unit tests would have to mock it. Nothing here imports anything at
 * runtime but React.
 */

import { createContext, useContext } from 'react';

export type GenieMessage = { role: 'user' | 'assistant'; text: string; isError?: boolean; rating?: number };

export interface GenieChatContextValue {
  messages: GenieMessage[];
  loading: boolean;
  /** Progress line while an answer is on its way ("Checking your records…"). */
  statusText: string;
  /** The last question sent, for Retry under a failed answer. */
  lastQuestion: string;
  send: (question: string) => Promise<void>;
  stop: () => void;
  clearThread: () => void;
  /** 1-3 star rating for the assistant answer at `index`. */
  rate: (index: number, rating: number) => void;

  /** Whether the bubble's chat panel is open. */
  panelOpen: boolean;
  /** Open the panel. `source` labels the parent_agent_view event, e.g. 'bubble'. */
  openPanel: (source: string) => void;
  closePanel: () => void;
}

export const GenieChatContext = createContext<GenieChatContextValue | null>(null);

/** The shared conversation. Only valid under `GenieChatProvider`. */
export function useGenieChat(): GenieChatContextValue {
  const ctx = useContext(GenieChatContext);
  if (!ctx) throw new Error('useGenieChat must be used inside GenieChatProvider');
  return ctx;
}
